import { useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera as CameraIcon, Upload, Loader2, Pencil, User as UserIcon, Linkedin } from 'lucide-react';
import { profilesAPI } from '../api/client';
import AvatarCropper from './AvatarCropper';
import WebcamCaptureModal from './WebcamCaptureModal';
import { isCameraCancel } from '../native/camera';

interface AvatarCaptureProps {
  avatarUrl?: string;
  onChange: (url: string) => void;
  // Photo pulled from a connected LinkedIn account, offered with a confirm step.
  linkedinPhotoUrl?: string;
}

// Profile photo is required (the directory is photo-based). Members can upload
// from their computer, take a photo with their device camera, or — if they
// connected LinkedIn — confirm the LinkedIn photo and still swap it out.
export default function AvatarCapture({ avatarUrl, onChange, linkedinPhotoUrl }: AvatarCaptureProps) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [draftUrl, setDraftUrl] = useState('');
  const [showWebcam, setShowWebcam] = useState(false);
  const sourceBlobRef = useRef<Blob | null>(null);

  const clearDraft = () => {
    if (draftUrl) URL.revokeObjectURL(draftUrl);
    setDraftUrl('');
  };

  const uploadFile = async (file: Blob, filename: string) => {
    setIsUploading(true);
    setError('');
    try {
      const { avatar_url } = await profilesAPI.uploadAvatar(file, filename, sourceBlobRef.current);
      onChange(avatar_url);
      return true;
    } catch {
      setError('Upload failed. Please try a different photo.');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const beginCrop = (file: Blob, asSource = false) => {
    if (asSource) sourceBlobRef.current = file;
    if (draftUrl) URL.revokeObjectURL(draftUrl);
    setError('');
    setDraftUrl(URL.createObjectURL(file));
  };

  const editCurrentPhoto = async () => {
    if (!avatarUrl || isUploading) return;
    setError('');
    if (sourceBlobRef.current) {
      beginCrop(sourceBlobRef.current);
      return;
    }
    try {
      const blob = await profilesAPI.downloadAvatar(true);
      sourceBlobRef.current = blob;
      beginCrop(blob);
      return;
    } catch {
      // Older photos have no stored original — fall back to the cropped file.
    }
    try {
      const blob = await profilesAPI.downloadAvatar();
      beginCrop(blob);
      return;
    } catch {
      // LinkedIn (or other remote) photos were never stored — try the URL directly.
    }
    try {
      const response = await fetch(avatarUrl);
      if (!response.ok) throw new Error('fetch');
      beginCrop(await response.blob());
    } catch {
      setError('Could not open that photo for editing. Upload it again.');
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) beginCrop(file, true);
    e.target.value = '';
  };

  const takePhoto = async () => {
    // On native, use the OS camera via Capacitor; in any browser with a
    // camera (desktop included) open a live getUserMedia capture; otherwise
    // fall back to a file input. Crop happens in-app in every path.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await Camera.getPhoto({
          quality: 80,
          resultType: CameraResultType.Uri,
          source: CameraSource.Camera,
          allowEditing: false,
        });
        if (photo.webPath) {
          const blob = await (await fetch(photo.webPath)).blob();
          beginCrop(blob, true);
        }
      } catch (err) {
        if (!isCameraCancel(err)) setError('Could not access the camera.');
      }
    } else if (typeof navigator.mediaDevices?.getUserMedia === 'function') {
      setError('');
      setShowWebcam(true);
    } else {
      cameraInputRef.current?.click();
    }
  };

  return (
    <div className="flex items-center gap-4">
      {avatarUrl ? (
        <button
          type="button"
          onClick={editCurrentPhoto}
          disabled={isUploading}
          aria-label="Edit profile photo"
          className="group relative h-24 w-24 shrink-0 rounded-full overflow-hidden bg-transparent border border-gray-200 disabled:opacity-60"
        >
          <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
          <span className="absolute inset-0 bg-navy/0 pointer-coarse:bg-navy/30 group-hover:bg-navy/45 flex items-center justify-center transition">
            <Pencil size={18} className="text-white opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100" />
          </span>
          {isUploading && (
            <span className="absolute inset-0 bg-navy/40 flex items-center justify-center">
              <Loader2 size={22} className="animate-spin text-white" />
            </span>
          )}
        </button>
      ) : (
        <div className="relative h-24 w-24 shrink-0 rounded-full overflow-hidden bg-transparent border border-gray-200 flex items-center justify-center">
          <UserIcon size={36} className="text-gray-300" />
          {isUploading && (
            <div className="absolute inset-0 bg-navy/40 flex items-center justify-center">
              <Loader2 size={22} className="animate-spin text-white" />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40 transition disabled:opacity-50"
          >
            <Upload size={15} />
            Upload photo
          </button>
          <button
            type="button"
            onClick={takePhoto}
            disabled={isUploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40 transition disabled:opacity-50"
          >
            <CameraIcon size={15} />
            Take a photo
          </button>
          {avatarUrl && (
            <button
              type="button"
              onClick={editCurrentPhoto}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 text-navy hover:border-navy/40 transition disabled:opacity-50"
            >
              <Pencil size={15} />
              Edit photo
            </button>
          )}
          {linkedinPhotoUrl && linkedinPhotoUrl !== avatarUrl && (
            <button
              type="button"
              onClick={() => onChange(linkedinPhotoUrl)}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-[#0a66c2]/30 text-[#0a66c2] hover:bg-[#0a66c2]/5 transition disabled:opacity-50"
            >
              <Linkedin size={15} />
              Use LinkedIn photo
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          A clear headshot helps members recognize you in the directory. Click your photo to adjust the crop.
        </p>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>

      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        onChange={onFilePicked}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={onFilePicked}
        className="hidden"
      />

      {showWebcam && (
        <WebcamCaptureModal
          onCapture={(blob) => {
            setShowWebcam(false);
            beginCrop(blob, true);
          }}
          onCancel={() => setShowWebcam(false)}
          onPickFileInstead={() => {
            setShowWebcam(false);
            cameraInputRef.current?.click();
          }}
        />
      )}

      {draftUrl && (
        <AvatarCropper
          src={draftUrl}
          confirming={isUploading}
          onCancel={clearDraft}
          onConfirm={async (blob) => {
            const uploaded = await uploadFile(blob, 'avatar.png');
            if (uploaded) clearDraft();
          }}
        />
      )}
    </div>
  );
}
